import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { OverviewSkeleton } from '@/components/home/OverviewSkeleton';

import { Deferred } from '@knitknot/shared/components/ui/deferred';
import { DetailPageSkeleton } from '@knitknot/shared/components/ui/detail-page-skeleton';

/**
 * THE console lock. A workspace that has not finished onboarding has nothing to
 * show on any data page, so it does not get to open one.
 *
 * It wraps `<Outlet/>` inside `ConsoleLayout`, which is what keeps the sidebar
 * and the top bar — workspace switcher, search, user profile, settings —
 * completely live: they are rendered OUTSIDE this. It also sits outside the
 * route-chunk `<Suspense>`, so a locked deep link never even imports the target
 * page's chunk.
 *
 * Before this existed, "you are not set up yet" had four implementations on four
 * pages (two of which rendered a wizard that told a gated workspace its research
 * was complete when it was parked at gate 1 of 8), `/runs` had none at all, and
 * hovering the sidebar prefetched six endpoints that all 400'd.
 */

/** Reachable while locked. Settings and the wizard, and nothing else. */
const ALLOWED_WHILE_LOCKED = ['/onboarding', '/settings', '/no-workspace'];

/**
 * How long we will hold a page back waiting for the lock answer.
 *
 * Holding avoids a flash of a data page that is about to be yanked away — but an
 * UNBOUNDED hold is itself a lock, and the onboarding query is not persisted, so
 * every cold load pays a round trip. If the answer has not arrived by now we let
 * the page through: failing open is the whole posture.
 */
const MAX_HOLD_MS = 3000;

export function OnboardingGate({
  workspaceId,
  locked,
  resolving,
  children,
}: {
  workspaceId?: string | null;
  locked: boolean;
  resolving: boolean;
  children: ReactNode;
}) {
  const { pathname } = useLocation();
  const [heldTooLong, setHeldTooLong] = useState(false);

  useEffect(() => {
    setHeldTooLong(false);
    if (!resolving) return;
    const t = setTimeout(() => setHeldTooLong(true), MAX_HOLD_MS);
    return () => clearTimeout(t);
  }, [workspaceId, resolving]);

  const allowed = ALLOWED_WHILE_LOCKED.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  if (allowed) return <>{children}</>;

  if (resolving && !heldTooLong) {
    if (pathname === '/overview') return <OverviewSkeleton />;
    // The same fallback the route-chunk Suspense uses, so a held page and a
    // loading page look identical rather than announcing the gate.
    return (
      <Deferred delay={200}>
        <div className="p-6">
          <DetailPageSkeleton />
        </div>
      </Deferred>
    );
  }

  // `replace`, and no `?next=`: the flow is eight steps and can span sessions, so
  // "we'll take you back to /sources/abc" is a promise we cannot keep. The wizard
  // owns its own terminus — it lands on /home when the last gate is approved.
  if (locked) return <Navigate to="/onboarding" replace />;

  return <>{children}</>;
}
